import * as React from "react";
import { RefreshCw, Loader2 } from "lucide-react";
import { usePullToRefresh } from "@/hooks/useMobileUtils";
import { cn } from "@/lib/utils";

/**
 * PullToRefresh - Pull-to-refresh container for mobile lists
 */
const PullToRefresh = React.forwardRef(
  (
    {
      className,
      children,
      onRefresh,
      threshold = 80,
      resistance = 2.5,
      pullText = "Pull to refresh",
      releaseText = "Release to refresh",
      refreshingText = "Refreshing...",
      disabled = false,
      ...props
    },
    ref,
  ) => {
    const {
      containerRef,
      handlers,
      pulling,
      pullDistance,
      refreshing,
      progress,
    } = usePullToRefresh(onRefresh, { threshold, resistance });

    // Combine refs
    const combinedRef = (node) => {
      containerRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    const indicatorOpacity = Math.min(progress * 1.5, 1);
    const indicatorScale = 0.5 + progress * 0.5;
    const rotation = progress * 180;

    const renderText = () => {
      if (refreshing) {
        return refreshingText;
      }
      return progress >= 1 ? releaseText : pullText;
    };

    return (
      <div
        ref={combinedRef}
        className={cn(
          "relative overflow-hidden",
          disabled && "pointer-events-none",
          className,
        )}
        {...(!disabled && handlers)}
        {...props}
      >
        {/* Pull indicator */}
        <div
          className="absolute left-0 right-0 flex items-center justify-center pointer-events-none z-10"
          style={{
            top: -60 + pullDistance,
            height: 60,
            opacity: indicatorOpacity,
            transform: `scale(${indicatorScale})`,
            transition: pulling ? "none" : "all 0.2s ease-out",
          }}
        >
          <div className="flex flex-col items-center gap-1">
            {refreshing ? (
              <Loader2 className="h-6 w-6 text-primary animate-spin" />
            ) : (
              <RefreshCw
                className="h-6 w-6 text-muted-foreground transition-transform"
                style={{ transform: `rotate(${rotation}deg)` }}
              />
            )}
            <span className="text-xs text-muted-foreground">
              {renderText()}
            </span>
          </div>
        </div>

        {/* Content with pull offset */}
        <div
          style={{
            transform: `translateY(${pullDistance}px)`,
            transition: pulling ? "none" : "transform 0.2s ease-out",
          }}
        >
          {children}
        </div>
      </div>
    );
  },
);
PullToRefresh.displayName = "PullToRefresh";

/**
 * SwipeableRow - Row with swipe actions (like iOS mail)
 */
const SwipeableRow = React.forwardRef(
  (
    {
      className,
      children,
      leftActions,
      rightActions,
      onSwipeLeft,
      onSwipeRight,
      threshold = 80,
      ...props
    },
    ref,
  ) => {
    const [offsetX, setOffsetX] = React.useState(0);
    const [swiping, setSwiping] = React.useState(false);
    const startX = React.useRef(0);
    const currentX = React.useRef(0);

    const handleTouchStart = (e) => {
      startX.current = e.touches[0].clientX;
      currentX.current = startX.current;
      setSwiping(true);
    };

    const handleTouchMove = (e) => {
      if (!swiping) return;

      currentX.current = e.touches[0].clientX;
      const diff = currentX.current - startX.current;

      // Apply resistance at edges
      const resistance = 0.5;
      let newOffset = diff * resistance;

      // Limit offset
      const maxOffset = 120;
      newOffset = Math.max(-maxOffset, Math.min(maxOffset, newOffset));

      setOffsetX(newOffset);
    };

    const handleTouchEnd = () => {
      setSwiping(false);

      if (Math.abs(offsetX) >= threshold) {
        if (offsetX > 0 && onSwipeRight) {
          onSwipeRight();
        } else if (offsetX < 0 && onSwipeLeft) {
          onSwipeLeft();
        }
      }

      // Reset position
      setOffsetX(0);
    };

    return (
      <div ref={ref} className={cn("swipe-container", className)} {...props}>
        {/* Left actions (revealed on swipe right) */}
        {leftActions && (
          <div
            className="swipe-actions left-0"
            style={{ width: Math.max(0, offsetX) }}
          >
            {leftActions}
          </div>
        )}

        {/* Right actions (revealed on swipe left) */}
        {rightActions && (
          <div
            className="swipe-actions"
            style={{ width: Math.max(0, -offsetX) }}
          >
            {rightActions}
          </div>
        )}

        {/* Main content */}
        <div
          className="swipe-content"
          style={{
            transform: `translateX(${offsetX}px)`,
            transition: swiping ? "none" : "transform 0.2s ease-out",
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {children}
        </div>
      </div>
    );
  },
);
SwipeableRow.displayName = "SwipeableRow";

/**
 * SwipeAction - Action button for swipeable row
 */
const SwipeAction = React.forwardRef(
  ({ className, variant = "default", icon: Icon, children, ...props }, ref) => {
    const variantClasses = {
      default: "bg-muted text-muted-foreground",
      destructive: "swipe-action-delete",
      archive: "swipe-action-archive",
      success: "bg-success text-success-foreground",
    };

    return (
      <button
        ref={ref}
        className={cn("swipe-action-btn", variantClasses[variant], className)}
        {...props}
      >
        {Icon && <Icon className="h-5 w-5" />}
        {children}
      </button>
    );
  },
);
SwipeAction.displayName = "SwipeAction";

export { PullToRefresh, SwipeableRow, SwipeAction };
